import {
  ANGLE_ROT_WARP,
  RADIUS_GALAXY,
  WAVE_SPEED,
  MAX_WARP_ANGLE,
  WAVE_START,
  INCLIN_GALAXY
} from '../configEntities/galaxyConfig.js'

import fragmentOrbit from '../../../shaders/orbits/fragment.glsl'
import vertexOrbit from '../../../shaders/orbits/vertex.glsl'

import { corAleatoria, rotateAroundAxisY, rotateAroundAxisX } from '../helpers.js';

import {
  BufferGeometry,
  ShaderMaterial,
  AdditiveBlending,
  BufferAttribute,
  Points,
  Quaternion, Vector3
} from 'three';

export function createOrbitCircle(
    orbitRadius,
    orbitInclin,
    ia,
    ifreq,
    ra,
    rfreq,
    argPeriapsis,
    ecc,
    direction
  ) {
    const segmentCount = 240;
    const positions = new Float32Array(segmentCount * 3);
    const angles    = new Float32Array(segmentCount);
    const alphas    = new Float32Array(segmentCount);
  
    for (let i = 0; i < segmentCount; i++) {
  
      const M = (i / segmentCount) * Math.PI * 2;
      let E = M, delta;
      do {
        delta = E - ecc * Math.sin(E) - M;
        E -= delta / (1 - ecc * Math.cos(E));
      } while (Math.abs(delta) > 1e-5);
  
      const theta = 2 * Math.atan2(
        Math.sqrt(1 + ecc) * Math.sin(E / 2),
        Math.sqrt(1 - ecc) * Math.cos(E / 2)
      );
      const H_osc   = orbitInclin + ia * Math.sin(ifreq * theta);
      const r_base  = (orbitRadius * (1 - ecc * ecc)) / (1 + ecc * Math.cos(theta));
      const r_osc   = r_base + ra * Math.sin(rfreq * theta);
      const x = r_osc * Math.sin(theta + argPeriapsis);
      const y = r_osc * Math.cos(theta) * Math.sin(H_osc);
      const z = r_osc * Math.cos(theta + argPeriapsis) * Math.cos(H_osc);
  
      positions[3 * i]     = x;
      positions[3 * i + 1] = y;
      positions[3 * i + 2] = z;
  
      angles[i] = M;
      const t = i / (segmentCount - 1);
      if (direction === 1) {
        alphas[i] = 1.0 - Math.pow(1.0 - t, 2.0);
      } else {
        alphas[i] = 1.0 - (1.0 - Math.pow(1.0 - t, 2.0));
      }
    }
    const geo = new BufferGeometry();
    geo.setAttribute('position',    new BufferAttribute(positions, 3));
    geo.setAttribute('vertexAngle', new BufferAttribute(angles,    1));
    geo.setAttribute('vertexAlpha', new BufferAttribute(alphas,    1));
  
    const material = new ShaderMaterial({
      uniforms: {
        baseColor: { value: corAleatoria() },
        uInvertAlpha: { value: false }
      },
      vertexShader:   vertexOrbit,
      fragmentShader: fragmentOrbit,
      blending:       AdditiveBlending,
      transparent:    false,
      depthWrite:     false,
      depthTest:      false,
    });
  
    const points = new Points(geo, material);
    points.userData = {
      orbitRadius,
      orbitInclin,
      ecc,
      ia,
      ifreq,
      ra,
      rfreq,
      argPeriapsis,
      centerX: 0,
      centerY: 0,
      centerZ: 0
    };
   
  
    return points;
  }
  

export function orbit({
  radius,
  angle,
  eccentricity: e,
  time,
  center,
  inclinationOsc: { amplitude: ia = 0.1, frequency: ifreq = 0.1 } = {},
  radiusOsc:      { amplitude: ra = 0.01, frequency: rfreq = 0.1 } = {},
  orientation:    { argPeriapsis, longAscNode, precessionPhase },
  warp:           { enabled = false, phase: warpPhase = 0 } = {},
  dynamicAxis = new Vector3(0,1,0),
  precessRotPhase = 0,
  alignToEquator = false
}) {
  const tol = 1e-4;
  let E = time, delta;
  do {
    delta = E - e * Math.sin(E) - time;
    E -= delta / (1 - e * Math.cos(E));
  } while (Math.abs(delta) > tol);

  const theta = 2 * Math.atan2(
    Math.sqrt(1 + e) * Math.sin(E / 2),
    Math.sqrt(1 - e) * Math.cos(E / 2)
  );

  const H_osc  = angle + ia * Math.sin(ifreq * theta);
  const r_base = (radius * (1 - e * e)) / (1 + e * Math.cos(theta));
  const r_osc  = r_base + ra * Math.sin(rfreq * theta);

  let x = r_osc * Math.sin(theta + argPeriapsis);
  let y = r_osc * Math.cos(theta) * Math.sin(H_osc);
  let z = r_osc * Math.cos(theta + argPeriapsis) * Math.cos(H_osc);

  ({ newX: x, newY: y, newZ: z } =
    rotateAroundAxisY(x, y, z, precessionPhase + longAscNode));

  if (enabled) {
    const dx = x, dz = z;
    const radXY = Math.hypot(dx, dz);
    if (radXY > WAVE_START) {
      const tNorm     = (radXY - WAVE_START) / (RADIUS_GALAXY - WAVE_START);
      const bendAngle = tNorm * MAX_WARP_ANGLE;
      const warpOff   = Math.sin(bendAngle) * (RADIUS_GALAXY * tNorm);
      const baseAng   = Math.atan2(dz, dx);
      const phase     = (baseAng + warpPhase + ANGLE_ROT_WARP) % (Math.PI * 2);
      y += warpOff * Math.cos(phase);
      ({ newX: x, newY: y, newZ: z } = rotateAroundAxisX(x, y, z, INCLIN_GALAXY));
    }
  }

  if (alignToEquator) {
    const q = new Quaternion().setFromUnitVectors(new Vector3(0, 1, 0), dynamicAxis);
      const pos = new Vector3(x, y, z).applyQuaternion(q);
    x = pos.x; y = pos.y; z = pos.z;
  }
  return {
    x0: x + center.x,
    y0: y + center.y,
    z0: z + center.z,
  };
}


export function orbitWarp(
  points,
  time,
  warp,
  precessionPhase = 0,
  warpPhase = 0,
  longAscNode = 0,
  dynamicAxis = new Vector3(0,1,0),
  precessRotPhase = 0,
  alignToEquator = false
) {

  const posAttr = points.geometry.attributes.position.array;
  const angles  = points.geometry.attributes.vertexAngle.array;
  const {
    orbitRadius: radius,
    orbitInclin: angle,
    ecc: e,
    ia, ifreq, ra, rfreq,
    argPeriapsis, centerX: cx, centerY: cy, centerZ: cz
  } = points.userData;
  const tol = 1e-4, N = angles.length;

  for (let i = 0; i < N; i++) {
    const M0 = angles[i], M = M0 + time;
    let E = M, delta = 1;
    while (Math.abs(delta) > tol) {
      delta = E - e * Math.sin(E) - M;
      E -= delta / (1 - e * Math.cos(E));
    }
    const theta = 2 * Math.atan2(
      Math.sqrt(1 + e) * Math.sin(E / 2),
      Math.sqrt(1 - e) * Math.cos(E / 2)
    );

    const H_osc  = angle + ia * Math.sin(ifreq * theta);
    const r_base = (radius * (1 - e * e)) / (1 + e * Math.cos(theta));
    const r_osc  = r_base + ra * Math.sin(rfreq * theta);

    let x = r_osc * Math.sin(theta + argPeriapsis);
    let y = r_osc * Math.cos(theta) * Math.sin(H_osc);
    let z = r_osc * Math.cos(theta + argPeriapsis) * Math.cos(H_osc);

    const rotY = rotateAroundAxisY(x, y, z, precessionPhase + longAscNode);
    x = rotY.newX; y = rotY.newY; z = rotY.newZ;

    if (warp) {
      const rXY = Math.hypot(x, z);
      if (rXY > WAVE_START) {
        const t   = (rXY - WAVE_START) / (RADIUS_GALAXY - WAVE_START);
        const bend = t * MAX_WARP_ANGLE;
        const off  = Math.sin(bend) * (RADIUS_GALAXY * t);
        const phase = (Math.atan2(z, x) + warpPhase + ANGLE_ROT_WARP) % (Math.PI * 2);
        y += off * Math.cos(phase);
      }
    }

    if (alignToEquator) {
      const q = new Quaternion().setFromUnitVectors(new Vector3(0, 1, 0), dynamicAxis);
      const pos = new Vector3(x, y, z).applyQuaternion(q);
      x = pos.x; y = pos.y; z = pos.z;
    }
    posAttr[3 * i]     = x + cx;
    posAttr[3 * i + 1] = y + cy;
    posAttr[3 * i + 2] = z + cz;
  }

  points.geometry.attributes.position.needsUpdate = true;
  points.material.needsUpdate = true;
}

